import Icon from 'components/icon'
import { EAssetSubtype, EAssetType, type FullAsset, type IAsset } from 'findef'
import FilecopyIcon from 'assets/img/svg/icons/file-copy2.svg?url'
import RocketIcon from 'assets/img/svg/icons/rocket.svg?url'
import BuildingIcon from 'assets/img/svg/icons/building.svg?url'
import GridIcon from 'assets/img/svg/icons/grid.svg?url'
// import BankAccountIcon from 'assets/img/svg/icons/bank_account.svg?url'
// import SavingsIcon from 'assets/img/svg/icons/savings.svg?url'
import QuestionIcon from 'assets/img/svg/icons/question_mark.svg?url'
// @ts-ignore
import IconReceipt from 'components/icons/IconReceipt.vue'
import type { IconFactor } from '@/types/elements/icon'
import type { FunctionalComponent } from 'vue'
import type { IIConProps } from 'components/icon/types'
import { twColor } from '@/utils/style'

export interface IAssetStyle {
  icon: IconFactor
  color: string
  colorSecondary: string
}

export type IAssetMapping = {
  [Prop in keyof typeof EAssetType]: {
    unlisted?: IAssetStyle
    listed?: IAssetStyle
    all?: IAssetStyle
  }
} & {
  [EAssetType.UNDEFINED]: {
    unlisted?: IAssetStyle
    listed?: IAssetStyle
    all: IAssetStyle
  }
}

const iconSize = 18

// dark
export const getAssetTypeSecondaryColor = (
  assetType: EAssetType,
  listed: boolean = false
): string => {
  switch (assetType) {
    case EAssetType.UNDEFINED:
      return '#E5E7EB'
    case EAssetType.EQUITY:
      return listed ? twColor('primary-600') : twColor('teal-400')
    case EAssetType.REAL_ESTATE:
      return twColor('purple-500')
    case EAssetType.CRYPTO:
      return twColor('orange-400')
    case EAssetType.DEBT:
      return twColor('pink-600')
    case EAssetType.COMMODITY:
      return twColor('primary-400')
    case EAssetType.CASH:
      return twColor('emerald-500')
    default:
    case EAssetType.ALTERNATIVE:
      return twColor('indigo-400')
  }
}

// bright
export const getAssetTypePrimaryColor = (
  assetType: EAssetType,
  listed: boolean = false
): string => {
  switch (assetType) {
    case EAssetType.UNDEFINED:
      return '#9CA3AF'
    case EAssetType.EQUITY:
      return listed ? twColor('primary-100') : twColor('green-100')
    case EAssetType.REAL_ESTATE:
      return twColor('purple-100')
    case EAssetType.CRYPTO:
      return twColor('orange-100')
    case EAssetType.DEBT:
      return twColor('pink-100')
    case EAssetType.COMMODITY:
      return twColor('primary-100')
    case EAssetType.CASH:
      return twColor('emerald-100')
    default:
    case EAssetType.ALTERNATIVE:
      return twColor('indigo-100')
  }
}

export const getAssetTypeColor = (assetType: EAssetType, listed: boolean = false) => {
  switch (assetType) {
    case EAssetType.REAL_ESTATE:
    case EAssetType.CRYPTO:
    case EAssetType.COMMODITY:
    case EAssetType.ALTERNATIVE:
    case EAssetType.EQUITY:
    case EAssetType.CASH:
      return getAssetTypeSecondaryColor(assetType, listed)
    default:
      return getAssetTypePrimaryColor(assetType, listed)
  }
}

export const mapping: IAssetMapping = {
  [EAssetType.UNDEFINED]: {
    all: {
      icon: (props: any) => (
        <Icon size={iconSize} src={GridIcon} {...props} fill="#9CA3AF" stroke="none" {...props} />
      ),
      color: '#E5E7EB',
      colorSecondary: '#9CA3AF'
    }
  },
  [EAssetType.EQUITY]: {
    unlisted: {
      icon: (props: any) => (
        <Icon size={iconSize} src={RocketIcon} {...props} fill="#16bdca" stroke="none" {...props} />
      ),
      color: '#def7ec',
      colorSecondary: '#16bdca'
    },
    listed: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={FilecopyIcon}
          {...props}
          fill="#1a56db"
          stroke="none"
          {...props}
        />
      ),
      color: '#e1effe',
      colorSecondary: '#1a56db'
    }
  },
  [EAssetType.CASH]: {
    unlisted: {
      icon: (props: any) => (
        <Icon size={iconSize} src={RocketIcon} {...props} fill={twColor('emerald-400')} stroke="none" {...props} />
      ),
      color: twColor('emerald-100'),
      colorSecondary: twColor('emerald-400')
    },
    listed: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={FilecopyIcon}
          {...props}
          fill={twColor('emerald-400')}
          stroke="none"
          {...props}
        />
      ),
      color: twColor('emerald-100'),
      colorSecondary: twColor('emerald-400')
    }
  },
  [EAssetType.DEBT]: {
    unlisted: {
      icon: (props: any) => (
        <Icon size={iconSize} src={RocketIcon} {...props} fill="#16bdca" stroke="none" {...props} />
      ),
      color: '#def7ec',
      colorSecondary: '#16bdca'
    },
    listed: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={FilecopyIcon}
          {...props}
          fill="#1a56db"
          stroke="none"
          {...props}
        />
      ),
      color: '#e1effe',
      colorSecondary: '#1a56db'
    }
  },
  [EAssetType.CRYPTO]: {
    unlisted: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={{
            src: async () => import('assets/img/svg/icons/coins.svg?raw')
          }}
          {...props}
          fill={getAssetTypeColor(EAssetType.CRYPTO)}
          stroke="none"
          {...props}
        />
      ),
      color: getAssetTypePrimaryColor(EAssetType.CRYPTO),
      colorSecondary: getAssetTypeSecondaryColor(EAssetType.CRYPTO)
    },
    listed: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={{
            src: async () => import('assets/img/svg/icons/coins.svg?raw')
          }}
          {...props}
          fill={getAssetTypeColor(EAssetType.CRYPTO)}
          stroke="none"
          {...props}
        />
      ),
      color: getAssetTypePrimaryColor(EAssetType.CRYPTO),
      colorSecondary: getAssetTypeSecondaryColor(EAssetType.CRYPTO)
    }
  },
  [EAssetType.COMMODITY]: {
    unlisted: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={{
            src: async () => import('assets/img/svg/icons/gold_bars.svg?raw')
          }}
          {...props}
          fill={getAssetTypeColor(EAssetType.COMMODITY)}
          stroke="none"
          {...props}
        />
      ),
      color: getAssetTypePrimaryColor(EAssetType.COMMODITY),
      colorSecondary: getAssetTypeSecondaryColor(EAssetType.COMMODITY)
    },
    listed: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={{
            src: async () => import('assets/img/svg/icons/gold_bars.svg?raw')
          }}
          {...props}
          fill={getAssetTypeColor(EAssetType.COMMODITY)}
          stroke="none"
          {...props}
        />
      ),
      color: getAssetTypePrimaryColor(EAssetType.COMMODITY),
      colorSecondary: getAssetTypeSecondaryColor(EAssetType.COMMODITY)
    }
  },
  [EAssetType.REAL_ESTATE]: {
    unlisted: {
      icon: (props: any) => (
        <Icon
          size={iconSize}
          src={BuildingIcon}
          {...props}
          fill="#7e3af2"
          stroke="none"
          {...props}
        />
      ),
      color: '#edebfe',
      colorSecondary: '#7e3af2'
    }
  },
  [EAssetType.ALTERNATIVE]: {
    unlisted: {
      icon: (props: any) => (
        <Icon size={iconSize} src={GridIcon} {...props} fill="#ff8a4c" stroke="none" {...props} />
      ),
      color: getAssetTypePrimaryColor(EAssetType.ALTERNATIVE),
      colorSecondary: getAssetTypeSecondaryColor(EAssetType.ALTERNATIVE)
    }
  }
}

export const getAssetIconComponent = (
  asset: Partial<IAsset | FullAsset>,
  iconProps: Partial<IIConProps> = {}
): FunctionalComponent<Partial<IIConProps>> => {
  if (!asset)
    return (props: any) => (
      <Icon
        size={iconSize}
        src={QuestionIcon}
        {...props}
        fill="#9CA3AF"
        stroke="none"
        {...iconProps}
        {...props}
      />
    )

  if (asset.isBankAccount) {
    if ((asset.subtypes || []).includes(EAssetSubtype.LOAN)) {
      return (props: any) => (
        <Icon
          size={iconSize}
          src={IconReceipt}
          {...props}
          fill="#16bdca"
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    }
    /* if ((asset.subtypes || []).includes(EAssetSubtype.SAVINGS_ACCOUNT)) {
      return (props: any) => (
        <Icon
          size={iconSize}
          src={SavingsIcon}
          {...props}
          fill="#16bdca"
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    }

    return (props: any) => (
      <Icon
        size={iconSize}
        src={BankAccountIcon}
        {...props}
        fill="#16bdca"
        stroke="none"
        {...iconProps}
        {...props}
      />
      ) */
  }

  switch (asset.type) {
    case EAssetType.EQUITY: {
      if (asset.listed) {
        return (props: any) => (
          <Icon
            size={iconSize}
            src={FilecopyIcon}
            fill={getAssetTypeColor(EAssetType.EQUITY, true)}
            stroke="none"
            {...iconProps}
            {...props}
          />
        )
      }
      if (!asset.listed) {
        return (props: any) => (
          <Icon
            size={iconSize}
            src={RocketIcon}
            fill={getAssetTypeColor(EAssetType.EQUITY, false)}
            stroke="none"
            {...iconProps}
            {...props}
          />
        )
      }
      return (props: any) => (
        <Icon
          size={iconSize}
          src={RocketIcon}
          fill={getAssetTypeColor(EAssetType.EQUITY)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    }
    case EAssetType.REAL_ESTATE:
      return (props: any) => (
        <Icon
          size={iconSize}
          src={BuildingIcon}
          fill={getAssetTypeColor(EAssetType.REAL_ESTATE)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    case EAssetType.CRYPTO:
      return (props: any) => (
        <Icon
          size={props.size || iconProps.size || iconSize}
          src={async () => import('assets/img/svg/icons/coins.svg?raw')}
          fill={getAssetTypeColor(EAssetType.CRYPTO)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    case EAssetType.COMMODITY:
      return (props: any) => (
        <Icon
          size={props.size || iconProps.size || iconSize}
          src={async () => import('assets/img/svg/icons/gold_bars.svg?raw')}
          fill={getAssetTypeColor(EAssetType.COMMODITY)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    case EAssetType.ALTERNATIVE:
      return (props: any) => (
        <Icon
          size={iconSize}
          src={GridIcon}
          fill={getAssetTypeColor(EAssetType.ALTERNATIVE)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    case EAssetType.CASH:
      return (props: any) => (
        <Icon
          size={iconSize}
          src={async () => import('assets/img/svg/icons/cash-bill.svg?raw')}
          fill={getAssetTypeColor(EAssetType.CASH)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    case EAssetType.DEBT:
      return (props: any) => (
        <Icon
          size={iconSize}
          src={async () => import('assets/img/svg/icons/receipt-bill.svg?raw')}
          fill={getAssetTypeColor(EAssetType.DEBT)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
    default:
    case EAssetType.UNDEFINED:
      return (props: any) => (
        <Icon
          size={iconSize}
          src={GridIcon}
          fill={getAssetTypeColor(EAssetType.UNDEFINED)}
          stroke="none"
          {...iconProps}
          {...props}
        />
      )
  }
}
